import React, { useContext, useState, useCallback } from "react";
import { useHistory, useLocation, RouteComponentProps } from "react-router-dom";
import DocumentTitle from "react-document-title";
import { ThemeContext } from "@fluentui/react-theme-provider";
import moment from "moment";
import { useBoolean } from "@fluentui/react-hooks";
import { MessageBar, MessageBarType } from "@fluentui/react";
import * as Lay from "./styles/RefundsStyles";
import * as S from "./styles/RequestRefundKeyStyles";
import Auth from "@/Auth/Auth";
import SignaturesService, { ISignature } from "@/services/SignaturesService";
import UserIdentitiesService from "@/services/UserIdentitiesService";
import { UserIdentityType } from "@/services/UserIdentitiesService";
import { SignatureComponent } from "@/components/Signature/SignatureComponent";
import ProcessingModalComponent from "@/components/Modals/ProcessingModalComponent";
import { getUsableMainIdentityId } from "@/utils/mainUserIdentityRules";

interface RequestRefundKeyProps extends RouteComponentProps {}

const processExpirationDate = (signature: ISignature): void =>
{
    if(signature && typeof signature.expiresAt === "string")
    {
        signature.expiresAt = moment(signature.expiresAt).toDate();
    }
};

const RequestRefundKey: React.FunctionComponent<RequestRefundKeyProps> = () =>
{
    const theme = useContext(ThemeContext);
    const history = useHistory();
    const { state } = useLocation<any>();
    const refundsFormState = state?.cancelledFormState ?? state;
    const user = Auth.getUserProfile();
    const [showSignatureForm, setShowSignatureForm] = useState(false);
    const [signatures, setSignatures] = useState<ISignature[]>([]);
    const [userIdentity, setUserIdentity] = useState<UserIdentityType | null>(null);
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [requestRegistered, setRequestRegistered] = useState(false);
    const [phoneFromBackend, setPhoneFromBackend] = useState<string | null>(null);
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);

    const goBackToRefunds = () =>
    {
        history.push("/refunds", refundsFormState);
    };

    // El principal vacío con el que nace todo usuario al registrarse cuenta como si no hubiera principal
    const usableMainIdentityId = getUsableMainIdentityId(user.mainUserIdentity);

    const handleRequest = useCallback(() =>
    {
        const mainIdentityId = usableMainIdentityId;
        if(mainIdentityId === undefined)
        {
            setAlertMessage("No se ha podido cargar la identidad. Inténtelo de nuevo más tarde.");
            return;
        }
        setAlertMessage(null);
        setLoading(true);
        setPhoneFromBackend(null);
        Promise.all([SignaturesService.createSignatureNoPlan(user.id, {useOperationPhone: true}), UserIdentitiesService.getUserIdentity(mainIdentityId)]).then(([sigResponse, identity]) =>
        {
            const data = (sigResponse as {data?: ISignature | ISignature[]})?.data;
            const sigs = Array.isArray(data) ? data : (data ? [data] : []);
            sigs.forEach(processExpirationDate);
            setSignatures(sigs);
            setUserIdentity(identity);
            const firstSig = sigs[0];
            if(firstSig?.phone != null && firstSig.phone !== "")
            {
                setPhoneFromBackend(firstSig.phone);
            }
            setShowSignatureForm(true);
        })
        .catch(() =>
        {
            setAlertMessage("Se ha producido un error al cargar el formulario de firma. Inténtelo de nuevo más tarde.");
        })
        .finally(() =>
        {
            setLoading(false);
        });
    }, [user.id, usableMainIdentityId]);

    const phoneNumber = phoneFromBackend != null && phoneFromBackend.length >= 3
        ? "X".repeat(phoneFromBackend.length - 3) + phoneFromBackend.slice(-3)
        : "";

    const handleSignatureError = useCallback(() =>
    {
        setAlertMessage("La firma ha fallado. Inténtelo de nuevo.");
    }, []);

    const handleSigned = useCallback((_response: unknown) =>
    {
        const signatureId = signatures[0]?.id;
        if(!userIdentity?.id || signatureId == null)
        {
            setAlertMessage("No se ha podido completar la solicitud. Inténtelo de nuevo más tarde.");
            return;
        }
        showModal();
        UserIdentitiesService.refundKeyRequest(signatureId, userIdentity.id).then(() =>
        {
            setRequestRegistered(true);
        })
        .catch(() =>
        {
            setAlertMessage("No se ha podido registrar la solicitud. Inténtelo de nuevo más tarde.");
        })
        .finally(() =>
        {
            hideModal();
        });
    }, [signatures, userIdentity?.id, showModal, hideModal]);

    return(
        <DocumentTitle title="IronIA - He olvidado mi Clave de Reembolso">
            <Lay.Main theme={theme}>
                <Lay.TitleContainer>
                    <h2>He olvidado mi Clave de Reembolso</h2>
                </Lay.TitleContainer>
                <Lay.SeparatorTop />
                {alertMessage != null &&
                (
                    <MessageBar messageBarType={MessageBarType.error}
                        isMultiline={false}
                        dismissButtonAriaLabel="Cerrar"
                        onDismiss={() => setAlertMessage(null)}
                    >
                        {alertMessage}
                    </MessageBar>
                )}
                {requestRegistered ?
                (
                    <React.Fragment>
                        <S.Body>
                            <S.SuccessMessage theme={theme}>
                                Se ha registrado tu solicitud para reestablecer la clave de reembolso. El equipo de soporte se pondrá en contacto contigo.
                            </S.SuccessMessage>
                            <S.SuccessActions>
                                <Lay.CardButton size="lg" type="button" onClick={goBackToRefunds}>
                                    Aceptar
                                </Lay.CardButton>
                            </S.SuccessActions>
                        </S.Body>
                    </React.Fragment>
                ) :
                (
                <React.Fragment>
                    <S.Body>
                        <S.Description theme={theme}>
                            Si no recuerdas tu clave de Reembolso aquí podrás solicitar que se reestablezca de nuevo.
                        </S.Description>
                        <S.Description theme={theme}>
                            Por motivos de seguridad, una vez realices la petición y formalices la solicitud, nos pondremos en contacto contigo para verificar tu identidad.
                        </S.Description>
                        <S.Description theme={theme}>
                            Por favor, guarda y recuerda el código de firma de esta solicitud.
                        </S.Description>
                        <S.Description theme={theme}>
                            Para proceder con la solicitud, pulsa el botón "Solicitar".
                        </S.Description>
                    </S.Body>
                    {showSignatureForm && userIdentity && signatures.length > 0 &&
                    (
                        <S.SignatureSection>
                            <SignatureComponent
                                userIdentities={[userIdentity]}
                                signatures={signatures}
                                title="Firma de la solicitud de clave de reembolso"
                                alreadySignedText="Tu solicitud de nueva clave está en proceso..."
                                submitButtonText="Firmar solicitud"
                                cancelButtonText="Cancelar"
                                headerTextArea={phoneNumber ? `Introduzca la clave SMS que le hemos enviado a ${phoneNumber} para validar la solicitud.` : "Introduzca la clave SMS que le hemos enviado para validar la solicitud."}
                                backButtonText="Volver"
                                requestNewCodeButtonText="Recibir nuevo código"
                                onBackButtonClicked={() => setShowSignatureForm(false)}
                                onCancelButtonClicked={() => setShowSignatureForm(false)}
                                onNewCodeSended={(sig: ISignature) =>
                                {
                                    processExpirationDate(sig);
                                    setSignatures([sig]);
                                }}
                                onSignatureChanged={(sig: ISignature) =>
                                {
                                    processExpirationDate(sig);
                                    setSignatures([sig]);
                                }}
                                onSigned={handleSigned}
                                onError={handleSignatureError}
                            />
                        </S.SignatureSection>
                    )}
                    {!showSignatureForm &&
                    (
                        <S.FooterArea>
                            <Lay.CardButton size="lg" type="button" onClick={goBackToRefunds}>
                                Volver
                            </Lay.CardButton>
                            <Lay.CardButton size="lg" type="button" onClick={handleRequest} disabled={loading}>
                                {loading ? "Cargando…" : "Solicitar"}
                            </Lay.CardButton>
                        </S.FooterArea>
                    )}
                </React.Fragment>
                )}
                <ProcessingModalComponent isModalOpen={isModalOpen} hideModal={hideModal} />
            </Lay.Main>
        </DocumentTitle>
    );
};

export default RequestRefundKey;